import type { BuiltPath } from '../../lib/buildMapPaths';
import electedData from '../../data/elected.json';
import './DistrictPanel.css';

type ElectedCandidate = { name: string; party: string };
const ELECTED = electedData as Record<string, ElectedCandidate[]>;

const PARTY_VAR: Record<string, string> = {
  PL: 'var(--party-pl)',
  PN: 'var(--party-pn)',
  AD: 'var(--party-ad)',
  IND: 'var(--party-ind)',
};
const partyVar = (p: string) => PARTY_VAR[p] ?? 'var(--muted)';

interface Props {
  district: BuiltPath | null;
}

export default function DistrictPanel({ district }: Props) {
  if (!district) return <EmptyState />;

  const r = district.properties.results_2022;
  const winner = r.winner;
  const margin = r.margin_pp;
  const winnerColor = partyVar(winner);
  const electedList = ELECTED[String(district.id)] ?? [];

  return (
    <div className="district-panel">
      <header className="district-panel__header" style={{ borderLeftColor: winnerColor }}>
        <p className="district-panel__kicker">District {district.id}</p>
        <h3 className="district-panel__title">
          {winner} won · {Math.abs(margin).toFixed(1)}pp
        </h3>
        <p className="district-panel__localities">
          {district.properties.localities.join(', ')}
        </p>
      </header>

      <div className="district-panel__body">
        <p className="district-panel__section-label">Vote Share · First Count</p>
        <div className="district-panel__bars">
          <BarRow label="PL" pct={r.pl_pct} color="var(--party-pl)" />
          <BarRow label="PN" pct={r.pn_pct} color="var(--party-pn)" />
          <BarRow label="ADPD" pct={r.adpd_pct} color="var(--party-ad)" />
          <BarRow label="Other" pct={r.other_pct} color="var(--muted)" />
        </div>

        <div className="district-panel__stats">
          <Stat label="Turnout" value={`${r.turnout_pct.toFixed(1)}%`} />
          <Stat label="Valid Votes" value={r.valid_votes.toLocaleString('en-MT')} />
          <Stat
            label="PL Votes"
            value={r.pl_votes.toLocaleString('en-MT')}
            color="var(--party-pl)"
          />
          <Stat
            label="PN Votes"
            value={r.pn_votes.toLocaleString('en-MT')}
            color="var(--party-pn)"
          />
        </div>

        <p className="district-panel__section-label">Elected Candidates</p>
        <ol className="district-panel__elected">
          {electedList.map((c, i) => (
            <ElectedRow key={`${c.name}-${i}`} rank={i + 1} name={c.name} party={c.party} />
          ))}
        </ol>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="district-panel district-panel--empty">
      <div className="district-panel__empty">
        <span className="district-panel__empty-icon" aria-hidden="true">
          ↗
        </span>
        <p className="district-panel__empty-text">
          Tap any district on the map to see its 2022 result and the MPs elected.
        </p>
      </div>
    </div>
  );
}

function BarRow({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div className="bar-row">
      <span className="bar-row__label">{label}</span>
      <span className="bar-row__bar">
        <span
          className="bar-row__fill"
          style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }}
        />
      </span>
      <span className="bar-row__pct">{pct.toFixed(1)}%</span>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="stat">
      <p className="stat__label">{label}</p>
      <p className="stat__value" style={color ? { color } : undefined}>
        {value}
      </p>
    </div>
  );
}

function ElectedRow({ rank, name, party }: { rank: number; name: string; party: string }) {
  const color = partyVar(party);
  return (
    <li className="elected-row" style={{ borderLeftColor: color }}>
      <span className="elected-row__rank">{rank}</span>
      <span className="elected-row__name">{name}</span>
      <span className="elected-row__party" style={{ background: color }}>
        {party}
      </span>
    </li>
  );
}
